import { GRADES } from "commom/consts/evaluations/consts-evaluations";
import getCriteriaHelpTexts from "commom/help-texts/criteria-help-texts";
import getEvaluationStatusColor from "commom/helpers/evaluation-status-colors";
import { getEvaluationStatusText } from "commom/helpers/evaluation-status-text";
import SquareRadioButton from "components/inputs/square-radio-button";
import useRequestState from "hooks/useRequestState";
import { useEffect, useLayoutEffect, useState } from "react";
import { Alert, Badge, Button, Card, Form, Modal, Spinner } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { AiFillInfoCircle } from "react-icons/ai";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { setStrGradesToNumber } from "services/form-process/process-evaluation-form";
import style from "./evaluation-form.module.css";

/**
 * @typedef {import("commom/contexts/useEvaluationContext").Sheet} Sheet
 * @typedef {import("commom/contexts/useEvaluationContext").Project} Project
 * 
 * @typedef EvaluationFormFields
 * 
 * @property {string} nota_planejamento
 * @property {string} nota_execucao
 * @property {string} nota_inovacao
 * @property {string} nota_atitude
 * @property {string} nota_criatividade
 * @property {string} nota_apresentacao
 * @property {string} nota_relatorio
 * @property {string} comments
 */

/**
 * modal to help users which methodology to use to evaluate a project.
 * @param {{ isShow: boolean, title: string, descriptions: string, onHide:()=> void }}
 *  isShow Used to hide or show the modal; 
 *  
 *  title: The modal title;
 * 
 *  descriptions: Contains information to assist the evaluator; It can be an array of strings or an array of nested objects.,
 *   
 *  onHide<function>: Callback called when the user closes the modal.
 * 
 * @returns 
 */
const ModalHelpEvaluation = ({
    isShow = false,
    title = "",
    descriptions = "",
    onHide
}) => {

    return (
        <Modal show={isShow} fullscreen={true} onHide={onHide}
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Apoio para avaliação
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <h5>
                    {title}
                </h5>
                <ul className={style.criteriaModalList}>
                  {descriptions.split("\n").map(item => <li>{item}</li>)}
                </ul>
            </Modal.Body>
        </Modal>
    )
}

const CriteriaGrades = ({ evaluationName, disabled = false, defaultValue, reactHookFormProps: { errors, control } }) => {
    const [selectedGrade, setSelectedGrade] = useState();

    useLayoutEffect(() => {
        if (defaultValue === "" || defaultValue === null || defaultValue === undefined) return;
        
        const grade = Number.parseInt(defaultValue);

        setSelectedGrade(grade);
    }, []);

    const onClick = event => {
        const grade = Number.parseInt(event.target.value);

        setSelectedGrade(grade);
    }

    return (
        <div>
            <div className="d-flex justify-content-between" data-testid={evaluationName}>
                {
                    GRADES.map(grade => (
                        <Controller
                            key={`${evaluationName}_${grade}`}
                            name={evaluationName}
                            rules={{
                                required: {
                                    value: true,
                                    message: `Atenção: critério não preenchido`
                                },

                            }}
                            control={control}
                            defaultValue={defaultValue}
                            render={({ field }) => (
                                <SquareRadioButton
                                    {...field}
                                    id={`${evaluationName}_${grade}`}
                                    value={grade}
                                    isSelected={selectedGrade === grade}
                                    onClick={onClick}
                                    disabled={disabled}
                                >
                                    {grade}
                                </SquareRadioButton>
                            )}
                        />

                    ))
                }
            </div>
            {errors[evaluationName] &&
                <Alert variant="danger" className="mt-2">
                    {errors[evaluationName].message}
                </Alert>
            }
        </div>
    )
}





/**
 * A form to evaluate a projet.
 * @param {{
 * sheet:Sheet,
 * project: Project,
 * onSuccessSubmit: (data: EvaluationFormFields)=> void;
 * onErrorSubmit: (fields: Object) => void,
 * defaultValues: EvaluationFormFields,
 * disabledFields: Array<'nota_n'|'comentario'> * 
 * }} param0 
 * @returns 
 */
const EvaluationForm = ({
    sheet,
    criteria,
    project,
    onSuccessSubmit,
    onErrorSubmit,
    defaultValues = {},
    canEdit,
    disabledFields = [] }) => {
    const { isLoading, setIsLoading } = useRequestState({ isLoading: false });
    const { register, handleSubmit, setValue, formState: { errors }, control, watch } = useForm();
    const [criteriaModal, setCriteriaModal] = useState({
        isShow: false,
        title: "",
        descriptions: ""
    });
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        if (!!location.hash) return;

        setCriteriaModal({
            isShow: false,
            title: "",
            descriptions: ""
        });
    }, [location]);

    const TOTAL_FIELDS = criteria.length


    /**
     * Called when the form data is valid.
     * @param {*} data - The form inputs. 
     */
    const onValidSubmit = async data => {
        /*Set the data to the format:
            {
                nota_{criteria_id}: 0,
                nota_{criteria_id}: 5,
                nota_{criteria_id}: 1,
                ...
            }
        */

            
        if (allFieldsDisabled) return;

        const gradesProcessed = setStrGradesToNumber(data);

        setIsLoading(true);
        // pass the valid data to callback
        onSuccessSubmit && await onSuccessSubmit({ ...gradesProcessed });
        setIsLoading(false);
    }

    /**
     * Callend when the submit has issues, like: invalid data.
     * @param {*} fields Fields with errors
     */
    const onInvalidSubmit = fields => {
        toast.error("Atenção! Não foi possível enviar sua avaliação, pois há erros no formulário. Verifique os campos e tente novamente.");

        // pass the fields with error to callback
        onErrorSubmit && onErrorSubmit(fields);
    }

    /**
     * Show a modal with  the informations about the clicked item
     */
    const onHelpCriteriaClick = (title, helpText) => {
        if (isLoading) return;

        // const criteria = getCriteriaHelpTexts({ projectCode: project.code })[whichCriteria];

        setCriteriaModal({
            isShow: true,
            title: title,
            descriptions: helpText
        });


        const { pathname } = location;

        navigate(`${pathname}#${title}`);
    }

    /**
     * Get a field default value.
     * @param {'nota_planejamento'|'nota_execucao'|'nota_inovacao'|'nota_criatividade'|
 * 'nota_apresentacao'|'nota_relatorio'|'comentario'} fieldName 
     * @returns 
     */
    const getDefaultValue = fieldName => {
        if (!(fieldName in defaultValues)) {
            return "";
        }

        return defaultValues[fieldName];
    }

    const watchCommentary = watch("comments", getDefaultValue("comments")) || "";

    const allFieldsDisabled = disabledFields.length === TOTAL_FIELDS;
    const editable = (typeof canEdit === 'boolean') ? canEdit : true;

    const hasProjectBox = Boolean(project.box)

    const projectDisplay = hasProjectBox ? `${project.box}` : project.code

    return (
        <>
            <Card>
                <Card.Header>
                    <b>Avaliação: {projectDisplay}</b>
                </Card.Header>
                <Card.Body>
                    <div>
                        <p className="m-0 fw-bold">
                            Status
                        </p>
                        <Badge bg={getEvaluationStatusColor(sheet?.status)}>
                            {getEvaluationStatusText(sheet?.status)}
                        </Badge>
                    </div>
                    <p className="mt-3 mb-0">
                        {project.title}
                    </p>
                </Card.Body>
            </Card>

            <Form onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)} className="py-2">
                <Card className="mb-3">
                    <Card.Header className="fw-bold text-uppercase">
                        <b>Critérios</b>
                    </Card.Header>
                    <Card.Body>
                        {criteria.map(criterion =>
                            <fieldset className="mb-3">
                                <legend>
                                    <Card.Title className="fw-normal">
                                        {criterion.description} <span role="button"><AiFillInfoCircle
                                            color="var(--lilac)"
                                            onClick={() => onHelpCriteriaClick(criterion.description, criterion.help_text)}
                                        /></span>
                                    </Card.Title>
                                </legend>
                                <CriteriaGrades
                                    defaultValue={getDefaultValue(`grade_${criterion.id}`)}
                                    disabled={isLoading || disabledFields.includes(`grade_${criterion.id}`)}
                                    evaluationName={`grade_${criterion.id}`}
                                    reactHookFormProps={{
                                        setValue,
                                        errors,
                                        control
                                    }}
                                />
                            </fieldset>)}
                    </Card.Body>
                </Card>

                <Card className="mb-3">
                    <fieldset>
                        <Card.Body>
                            <legend>
                                <b>Comentários para os estudantes</b>
                            </legend>

                            <Form.Floating>
                                <Form.Control
                                    id="comments"
                                    as="textarea"
                                    defaultValue={getDefaultValue("comments")}
                                    disabled={isLoading || disabledFields.includes("comments")}
                                    style={{ height: 150 }}
                                    maxLength={500}
                                    {...register('comments', {
                                        maxLength: {
                                            message: "Atenção, o comentário deve ter no máximo 500 caracteres",
                                            value: 500
                                        }
                                    })}
                                />
                                <label htmlFor="comments">Comentários</label>
                            </Form.Floating>
                            <span>
                                {`${watchCommentary}`.length}/500
                            </span>
                            {errors.comments &&
                                <Alert variant="danger" className="mt-2">
                                    {errors.comments.message}
                                </Alert>
                            }


                            {!allFieldsDisabled &&
                                <Button
                                    disabled={isLoading || !editable}
                                    type="submit"
                                    variant="lilac"
                                    className="w-100 mt-3">
                                    {isLoading ?
                                        <div>
                                            <Spinner
                                                animation="grow"
                                                variant="light"
                                                size="sm"
                                                className="me-3"
                                            />
                                            Aguarde...
                                        </div>
                                        : (!editable ? 'Somente leitura' : 'Salvar e continuar')}
                                </Button>
                            }
                        </Card.Body>
                    </fieldset>
                </Card>

            </Form>

            <ModalHelpEvaluation
                descriptions={criteriaModal.descriptions}
                isShow={criteriaModal.isShow}
                title={criteriaModal.title}
                onHide={() => {
                    setCriteriaModal({
                        descriptions: "",
                        isShow: false,
                        title: ""
                    });

                    navigate(-1);
                }
                }
            />


        </>
    )
}

export default EvaluationForm;
